import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "../styles/EditWorkflowPage.module.css";
import { AuthContext } from "../AuthProvider";
import { useSnackbar } from "notistack";
import { getWorkflowInfoById, updateWorkflow } from "../api/api";

const EditWorkflowPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { keycloak, authenticated } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkflow = async () => {
      try {
        if (!authenticated) {
          navigate("/");
          return;
        }

        setLoading(true);
        const wfInfo = await getWorkflowInfoById(id);

        // Check if current user is the owner
        const userId = keycloak.tokenParsed.sub;
        const isAdmin = keycloak.tokenParsed.realm_access.roles.includes("admin");
        if (userId !== wfInfo.user_id && !isAdmin) {
          navigate(`/workflow/${id}`);
          return;
        }

        setName(wfInfo.name || "");
        setDescription(wfInfo.description || "");
        setTags(wfInfo.tags || "");
        setLoading(false);
      } catch (error) {
        console.error("Error fetching workflow:", error);
        enqueueSnackbar("Failed to load workflow data", {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [id, authenticated, keycloak, navigate, enqueueSnackbar]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updatedWorkflow = {
        name,
        description,
        tags,
      };

      const success = await updateWorkflow(id, updatedWorkflow, keycloak.token);

      if (success) {
        enqueueSnackbar("Workflow updated successfully!", {
          variant: "success",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
        navigate(`/workflow/${id}`);
      } else {
        enqueueSnackbar("Failed to update workflow", {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "center",
          },
        });
      }
    } catch (error) {
      console.error("Error updating workflow:", error);
      enqueueSnackbar("An error occurred while updating the workflow", {
        variant: "error",
        anchorOrigin: {
          vertical: "top",
          horizontal: "center",
        },
      });
    }
  };

  if (loading)
    return <div className={styles.loading}>Loading workflow data...</div>;

  return (
    <div className={styles.editWorkflowContainer}>
      <h2>Edit Workflow</h2>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="name">Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="tags">Tags (comma separated)</label>
          <input
            type="text"
            id="tags"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g. AI, Machine Learning, NLP"
          />
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            onClick={() => navigate(`/workflow/${id}`)}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button type="submit" className={styles.saveButton}>
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditWorkflowPage;
